import React, { useCallback, useState } from 'react'
import {
  AddressFields,
  AmountField,
  CompanyFields,
  DonationFormCaptcha,
  GeneralError,
  HolderFields,
  HolderHeadline,
  MatchingFundBanner,
  OfflineMessage,
  PaymentMethodError,
  PaymentMethodSelection,
  RecurringSelection,
  SSLLogo,
  StripeCCFields,
  StripeSEPADebitFields,
  SubmitButton,
  Summary,
  Terms,
} from '../../shared'
import { Debug } from '../../debug'
import { Form, connect } from 'formik'
import { FormValues } from 'donate/types'
import { I18nHtml, handleError, postMessageScrollTop } from 'shared'
import { IFrameFormProps, StepProps } from './types'
import { ProhibitedNotice } from '../prohibited_notice'
import { TaxDeductibilityNotice } from '../tax_deductibility_notice'

export const IframeForm = (props: IFrameFormProps): JSX.Element => {
  const { config } = props
  if (config.donationsProhibited) return <ProhibitedNotice />

  return (
    <>
      <Form>
        <Step1 {...props} setFieldValue={props.setFieldValue} visible={props.values._step === 1} />
        <Step2 {...props} setFieldValue={props.setFieldValue} visible={props.values._step === 2} />
      </Form>
      <Debug />
    </>
  )
}

const TENNIS_HINT_STYLE: React.CSSProperties = {
  padding: '1rem',
  border: '1px solid #23035e',
  color: 'white',
  background: '#23035e',
  borderRadius: 4,
  marginTop: '1rem',
}
const TENNIS_HINT_LINK_STYLE: React.CSSProperties = { color: 'white', fontWeight: 'normal' }

const Step1 = connect<StepProps, FormValues>(
  ({
    formik: { setFieldTouched, initialValues, values, validateForm, isValid },
    display,
    config,
    setFieldValue,
    visible,
  }) => {
    const [step1SubmitCount, setStep1SubmitCount] = useState(0)

    const tennisHint =
      initialValues.receiver_type === 'FundraisingEvent' &&
      ['401', '35032', '35033'].includes(`${initialValues.receiver_id}`) &&
      values.amount_cents >= 100000
    const validateAndGoToNextStep = useCallback(() => {
      validateForm()
        .then((errors) => {
          postMessageScrollTop()
          if (isValid) {
            setFieldValue('_step', 2)
            return
          }
          // mark fields as touched so errors will show on them
          Object.keys(errors).forEach((k) => setFieldTouched(k))
          // increase step1SubmitCount for GeneralError
          setStep1SubmitCount((cnt) => cnt + 1)
        })
        .catch((error) => {
          handleError(error, i18n.t('errors.messages.validate'))
        })
    }, [validateForm, isValid, setFieldValue, setFieldTouched])

    return (
      <div style={{ display: visible ? 'block' : 'none' }}>
        <div className="donate-iframe-header">
          <div className="donate-iframe-headline">
            <p className="line1">{i18n.t('donate.iframe.headline')}</p>
            <p className="line2">(1/2)</p>
          </div>
        </div>
        <div className="donate-iframe-content generic-popover-constraints">
          <div className="max-w-screen-xs mx-auto p-3">
            <GeneralError currentSubmitCount={step1SubmitCount} />
            <OfflineMessage />

            <h4>{i18n.t('donate.iframe.recurring_headline')}</h4>
            <RecurringSelection />

            <h4>{i18n.t('donate.iframe.amount_headline')}</h4>
            <fieldset className="iframe-donations-donation-amount">
              <AmountField />
            </fieldset>

            {tennisHint && (
              <div style={TENNIS_HINT_STYLE}>
                <span>
                  Vielen Dank für deine große Unterstützungsbereitschaft! Über dieses Formular kannst du bis 1.000 €
                  spenden. Bitte wende dich zur Tätigung einer darüber hinausgehenden Spende direkt an Patrick und sein
                  Team beim Spendensupport von #AdvantageWe
                </span>
                <br />
                <a href="mailto:spendenservice@advantagewe.de" style={TENNIS_HINT_LINK_STYLE}>
                  spendenservice@advantagewe.de
                </a>
                <br />
                <span>+49 (0)89 12229572</span>
                <br />
                <br />
                <span>
                  Dort erfährst du alles zur Zahlungsabwicklung deines Wunschbetrags und auch zu den Möglichkeiten, noch
                  mehr aus deinem Engagement zu machen.
                </span>
              </div>
            )}

            <PaymentMethodSelection />

            <MatchingFundBanner config={config} display={display} />
            <HolderHeadline />
            <HolderFields />
            <CompanyFields />
            <AddressFields required />
            <StripeCCFields />
            <StripeSEPADebitFields />
            <PaymentMethodError />
            <DonationFormCaptcha config={config} />

            {!tennisHint && (
              <div className="my-3 mt-6 flex justify-between items-center">
                <button className="btn btn-primary btn-large" type="button" onClick={validateAndGoToNextStep}>
                  {i18n.t('donate.iframe.continue_button')}
                </button>
                <SSLLogo className="h-16" />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)

const Step2 = connect<StepProps, FormValues>(({ display, config, setFieldValue, visible }) => {
  const goBack: React.MouseEventHandler = useCallback(
    (event) => {
      event.preventDefault()
      postMessageScrollTop()
      setFieldValue('_step', 1)
    },
    [setFieldValue]
  )
  return (
    <div style={{ display: visible ? 'block' : 'none' }}>
      <div className="donate-iframe-header">
        <div className="donate-iframe-header-wrapper max-w-screen-xs mx-auto">
          <div className="donate-iframe-headline">
            <p className="line1">{i18n.t('donate.iframe.headline')}</p>
            <p className="line2">(2/2)</p>
          </div>
          <button type="button" className="back-link ml-3 cursor-pointer" onClick={goBack}>
            {i18n.t('donate.iframe.back_link')}
          </button>
        </div>
      </div>
      <div className="donate-iframe-content">
        <div className="max-w-screen-xs mx-auto p-3">
          <Summary
            config={config}
            display={display}
            explanation={
              <I18nHtml as="div" className="explanation-codonation" i18nKey="donate.iframe.codonation_intro_html" />
            }
          />
          <div className="my-3 mt-6 flex justify-between items-center">
            <SubmitButton config={config} />
            <SSLLogo className="h-16" />
          </div>
          <TaxDeductibilityNotice />
          <br />
          <Terms />
        </div>
      </div>
    </div>
  )
})
